import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { i18n as I18n } from 'i18next';
import { useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { HttpError, NetworkError } from './api/http.ts';
import { createAppI18n } from './i18n.ts';

const MAX_RETRIES = 2;

/** Retries only failures that can go away by themselves: no response, or a 5xx. */
export function shouldRetry(failureCount: number, error: Error): boolean {
  const transient =
    error instanceof NetworkError || (error instanceof HttpError && error.status >= 500);
  return transient && failureCount < MAX_RETRIES;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: { queries: { retry: shouldRetry, refetchOnWindowFocus: false } },
  });
}

export type AppProvidersProps = {
  children: ReactNode;
  /** Tests pass their own instances. */
  queryClient?: QueryClient;
  i18n?: I18n;
};

export function AppProviders({ children, queryClient, i18n }: AppProvidersProps) {
  const [client] = useState(() => queryClient ?? createQueryClient());
  const [translations] = useState(() => i18n ?? createAppI18n());
  return (
    <QueryClientProvider client={client}>
      <I18nextProvider i18n={translations}>{children}</I18nextProvider>
    </QueryClientProvider>
  );
}
